import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';

import brandingService, { BrandingData } from '../utils/api/brandingService';
import { generateFullTheme } from '../utils/brandingUtils';

interface BrandingContextType {
  brandData: BrandingData | null;
  isLoading: boolean;
  error: string | null;
  refreshBranding: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

/**
 * Injects theme CSS variables into the document root.
 * @param themeConfig - The theme configuration object containing color definitions.
 */
const applyThemeToDocument = (themeConfig: any) => {
  if (!themeConfig) return;

  // Generate full theme variables (light and dark)
  const fullTheme = generateFullTheme(themeConfig);

  // Determine current mode (default to light or get from localStorage)
  const currentMode = localStorage.getItem('globalTheme') || 'light';
  const themeVariables = fullTheme[currentMode] || fullTheme.light;

  if (themeVariables) {
    Object.entries(themeVariables).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value as string);
    });
  }
};

/**
 * BrandingProvider component that fetches and manages runtime branding data.
 * @param props - Component properties.
 * @param props.children - Child elements.
 * @returns The provider component.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const [brandData, setBrandData] = useState<BrandingData | null>(
    brandingService.getCachedBranding(),
  );
  const [isLoading, setIsLoading] = useState<boolean>(!brandData);
  const [error, setError] = useState<string | null>(null);

  // Apply theme immediately if we have cached data
  useEffect(() => {
    if (brandData?.theme) {
      applyThemeToDocument(brandData.theme);
    }
  }, []);

  /**
   * Fetches branding data from the API and updates state.
   */
  const fetchBranding = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await brandingService.fetchBranding();
      if (data) {
        setBrandData(data);
        applyThemeToDocument(data.theme);
      }
      setError(null);
    } catch (error_) {
      setError('Failed to load branding');
      console.error(error_);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  useEffect(() => {
    /**
     * Handles storage changes to re-apply themes if the global mode changes.
     * @param event - The storage event.
     */
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'globalTheme' && brandData?.theme) {
        applyThemeToDocument(brandData.theme);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [brandData?.theme]);

  // Re-apply theme if brandData changes
  useEffect(() => {
    if (brandData?.theme) {
      applyThemeToDocument(brandData.theme);
    }
  }, [brandData]);

  const contextValue = useMemo(
    () => ({
      brandData,
      isLoading,
      error,
      refreshBranding: fetchBranding,
    }),
    [brandData, isLoading, error, fetchBranding],
  );

  return <BrandingContext.Provider value={contextValue}>{children}</BrandingContext.Provider>;
}

/**
 * Custom hook to consume branding data from the BrandingContext.
 * @returns The branding context data.
 */
export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
